import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { TrendingUp, TrendingDown, ExternalLink, Eye, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const PublicUserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [setups, setSetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  const fetchUserData = async () => {
    try {
      // Fetch user info and setups in parallel
      const [infoResponse, setupsResponse] = await Promise.all([
        axios.get(`${backendUrl}/api/users/${userId}/info`),
        axios.get(`${backendUrl}/api/users/${userId}/public-setups`)
      ]);
      
      setUserInfo(infoResponse.data);
      setSetups(setupsResponse.data);
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Ошибка загрузки данных пользователя');
    } finally {
      setLoading(false);
    }
  };

  const copyShareLink = (setupId) => {
    const link = `${window.location.origin}/setup/${setupId}`;
    navigator.clipboard.writeText(link);
    toast.success('Ссылка скопирована в буфер обмена');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Header />
        <div className="pt-20 pb-8 px-4 md:px-6 max-w-7xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-800 rounded w-1/4"></div>
            <div className="h-64 bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!userInfo) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Toaster position="top-right" />
        <Header />
        <div className="pt-20 pb-8 px-4 md:px-6 max-w-7xl mx-auto">
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6 text-center">
              <p className="text-gray-400 mb-4">Пользователь не найден</p>
              <Button
                onClick={() => navigate('/leaderboard')}
                className="bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black hover:opacity-90"
              >
                К лидерборду
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const stats = userInfo.stats || {};

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Toaster position="top-right" />
      <Header />
      
      <div className="pt-20 pb-8 px-4 md:px-6 max-w-7xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/leaderboard')}
          className="mb-4 text-gray-400 hover:text-white hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Назад к лидерборду
        </Button>

        {/* User Info */}
        <Card className="bg-[#1a1a1a] border-gray-800 mb-8">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <Avatar className="h-24 w-24 border-2 border-[#00ff88]">
                <AvatarImage src={userInfo.picture} alt={userInfo.name} />
                <AvatarFallback className="bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black text-3xl">
                  {userInfo.name?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{userInfo.name}</h1>
                <p className="text-gray-400 mb-4">Публичные сетапы этого трейдера</p>
                
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-400">Всего сетапов</p>
                    <p className="text-2xl font-bold text-white">{stats.total_setups || 0}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-400">Успешные</p>
                    <p className="text-2xl font-bold text-[#00ff88]">{stats.successful_setups || 0}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-400">Общий профит</p>
                    <p className={`text-2xl font-bold ${(stats.total_profit || 0) >= 0 ? 'text-[#00ff88]' : 'text-red-500'}`}>
                      ${(stats.total_profit || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Public Setups */}
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white mb-4">
            Публичные сетапы ({setups.length})
          </h2>
          
          {setups.length === 0 ? (
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardContent className="pt-6 text-center text-gray-400">
                <Eye className="h-12 w-12 mx-auto mb-4 text-gray-600" />
                <p>У этого пользователя пока нет публичных сетапов</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {setups.map((setup) => {
                const finalBalance = setup.steps[setup.steps.length - 1]?.balance_after || setup.initial_balance;
                const profit = finalBalance - setup.initial_balance;
                const profitPercent = ((profit / setup.initial_balance) * 100).toFixed(2);
                
                const totalPoints = setup.steps.length * 2;
                const reachedPoints = setup.steps.reduce((sum, step) => {
                  let points = 0;
                  if (step.entry_reached) points += 1;
                  if (step.reached) points += 1;
                  return sum + points;
                }, 0);

                return (
                  <Card key={setup.id} className="bg-[#1a1a1a] border-gray-800 hover:border-[#00ff88]/50 transition-colors">
                    <CardHeader>
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
                        <div className="flex-1">
                          <CardTitle className="text-white text-lg md:text-xl">{setup.title}</CardTitle>
                          <p className="text-sm text-gray-400 mt-1">
                            Создан: {new Date(setup.created_at).toLocaleDateString('ru-RU')}
                          </p>
                        </div>
                        <Badge 
                          variant="outline" 
                          className={`${
                            setup.status === 'completed' 
                              ? 'bg-[#00ff88]/20 text-[#00ff88] border-[#00ff88]/50' 
                              : setup.status === 'active'
                              ? 'bg-blue-500/20 text-blue-400 border-blue-500/50'
                              : 'bg-red-500/20 text-red-400 border-red-500/50'
                          }`}
                        >
                          {setup.status === 'completed' ? 'Завершён' : setup.status === 'active' ? 'Активный' : 'Провален'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-400">Вложения</p>
                          <p className="text-lg font-semibold text-white">${setup.initial_balance.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Потенциал</p>
                          <p className="text-lg font-semibold text-white">${finalBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Профит</p>
                          <p className={`text-lg font-semibold ${profit >= 0 ? 'text-[#00ff88]' : 'text-red-500'}`}>
                            {profit >= 0 ? <TrendingUp className="inline h-4 w-4 mr-1" /> : <TrendingDown className="inline h-4 w-4 mr-1" />}
                            ${profit.toFixed(2)} ({profitPercent}%)
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Шагов</p>
                          <p className="text-lg font-semibold text-white">{setup.steps.length}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Прогресс</p>
                          <p className="text-lg font-semibold text-[#00ff88]">{reachedPoints}/{totalPoints}</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/setup/${setup.id}`)}
                          className="border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Открыть
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyShareLink(setup.id)}
                          className="border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800"
                        >
                          Скопировать ссылку
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicUserProfile;
